import { flexRender, Row, Table } from '@tanstack/react-table';
import React, { FC, Fragment, ReactElement } from 'react';

import { isMessageType } from '@savgroup-front-common/types';

import { menuAnimation } from '../../../animation/menuAnimation';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';

import { $StyledTd, $StyledTr, $SubTr } from './DatatableCore.styles';

interface TableBodyProps<D> {
  table: Table<D>;
  SubComponent?: FC<{ row: Row<D> }>;
  isSubRowStyled?: boolean;
  isExpandedBackgroundEnabled?: boolean;
  onClickRow?: (row: any) => void;
  dataTestId?: string;
}
const Body = <D = unknown,>({
  table,
  SubComponent,
  isSubRowStyled = true,
  isExpandedBackgroundEnabled = true,
  onClickRow,
  dataTestId,
}: TableBodyProps<D>): ReactElement => {
  const { rows } = table.getRowModel();

  return (
    <>
      {rows.map((row, rowIndex) => {
        const isExpanded = row.getIsExpanded();
        const visibleCells = row.getVisibleCells();
        const { subRows } = row;

        if (row.depth !== 0) {
          return <Fragment key={row.id} />;
        }

        return (
          <Fragment key={row.id}>
            <$StyledTr
              $isExpanded={isExpanded}
              $isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
              $isCliquable={!!onClickRow}
              onClick={() => (onClickRow ? onClickRow(row) : undefined)}
              {...menuAnimation}
              onMouseDown={(event: React.MouseEvent<HTMLTableRowElement>) => {
                if (event.button === 1 && onClickRow) {
                  onClickRow(row);
                }

                return;
              }}
            >
              {visibleCells.map((cell, cellIndex) => {
                const value = cell.getValue();

                const cellContext = cell.getContext();

                return (
                  <$StyledTd
                    key={cell.id}
                    $size={cell.column.getSize()}
                    $minSize={cell.column.columnDef.minSize}
                    $maxSize={cell.column.columnDef.maxSize}
                    data-testid={
                      dataTestId
                        ? `cell_${dataTestId}_${rowIndex}_${cellIndex}`
                        : undefined
                    }
                  >
                    {isMessageType(value) ? (
                      <SafeFormattedMessageWithoutSpread message={value} />
                    ) : (
                      flexRender(cell.column.columnDef.cell, cellContext)
                    )}
                  </$StyledTd>
                );
              })}
            </$StyledTr>
            {!SubComponent &&
              isExpanded &&
              isSubRowStyled &&
              subRows.map((subRow) => {
                return (
                  <$StyledTr key={subRow.id} {...menuAnimation}>
                    {subRow.getVisibleCells().map((cell) => {
                      const value = cell.getValue();
                      const cellContext = cell.getContext();

                      return (
                        <$StyledTd
                          key={cell.id}
                          $size={cell.column.getSize()}
                          $minSize={cell.column.columnDef.minSize}
                          $maxSize={cell.column.columnDef.maxSize}
                        >
                          {isMessageType(value) ? (
                            <SafeFormattedMessageWithoutSpread
                              message={value}
                            />
                          ) : (
                            flexRender(cell.column.columnDef.cell, cellContext)
                          )}
                        </$StyledTd>
                      );
                    })}
                  </$StyledTr>
                );
              })}
            {SubComponent && isExpanded && isSubRowStyled && (
              <$SubTr
                $isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
                $isCliquable={!!onClickRow}
                onClick={() => (onClickRow ? onClickRow(row) : undefined)}
                {...menuAnimation}
              >
                <td colSpan={visibleCells.length}>
                  <SubComponent row={row} />
                </td>
              </$SubTr>
            )}

            {SubComponent && isExpanded && !isSubRowStyled && (
              <SubComponent row={row} />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default Body;
